import { useMemo, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { useStats } from '@/store/stats';
import { useSettings } from '@/store/settings';
import { VARIANTS, getVariant } from '@/engine/variants';
import type { VariantId } from '@/engine/types';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/stats')({
  component: StatsPage,
});

const dateFmt = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

function StatsPage() {
  const records = useStats(s => s.records);
  const highScores = useStats(s => s.highScores);
  const clear = useStats(s => s.clear);
  const playerName = useSettings(s => s.playerName);
  const [clearOpen, setClearOpen] = useState(false);

  const played = useMemo(
    () =>
      VARIANTS.filter(
        v =>
          (highScores[v.id]?.length ?? 0) > 0 ||
          records.some(r => r.variantId === v.id)
      ),
    [highScores, records]
  );
  const [variantId, setVariantId] = useState<VariantId | null>(null);
  const activeVariant = variantId ?? played[0]?.id ?? null;

  const wins = records.filter(r =>
    r.players.some(p => p.won && p.name === playerName)
  ).length;
  const best = records.reduce(
    (b, r) =>
      Math.max(b, ...r.players.filter(p => p.kind !== 'ai').map(p => p.score)),
    0
  );

  if (records.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-6 sm:py-8">
        <h1 className="mb-6 font-display text-2xl tracking-wider">Stats</h1>
        <Card className="p-8 text-center">
          <p className="font-display text-lg tracking-wide">
            No games on the board yet
          </p>
          <p className="mt-2 text-sm text-muted">
            Finish a game and it shows up here with high scores per variant.
          </p>
          <Link to="/" className={cn(buttonVariants({ size: 'lg' }), 'mt-5')}>
            Start a game
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:py-8">
      <div className="mb-6 flex items-baseline justify-between">
        <h1 className="font-display text-2xl tracking-wider">Stats</h1>
        <Button variant="ghost" size="sm" onClick={() => setClearOpen(true)}>
          Clear stats
        </Button>
      </div>

      <div className="mb-8 grid grid-cols-3 gap-2 sm:gap-3">
        <Tile label="Games played" value={records.length} />
        <Tile label={`Wins for ${playerName}`} value={wins} />
        <Tile label="Best score" value={best} />
      </div>

      {activeVariant && (
        <section className="mb-8">
          <h2 className="mb-3 font-display text-lg tracking-wide">
            High scores
          </h2>
          <div
            className="mb-3 flex flex-wrap gap-2"
            role="tablist"
            aria-label="Variant"
          >
            {played.map(v => (
              <button
                key={v.id}
                type="button"
                role="tab"
                aria-selected={v.id === activeVariant}
                onClick={() => setVariantId(v.id)}
                className={cn(
                  'h-8 rounded-full border px-3 text-sm font-semibold',
                  v.id === activeVariant
                    ? 'border-accent bg-accent/15 text-accent'
                    : 'border-line text-muted hover:text-text'
                )}
              >
                {v.name}
              </button>
            ))}
          </div>
          <Card className="divide-y divide-line">
            {(highScores[activeVariant] ?? []).length === 0 && (
              <p className="p-4 text-sm text-muted">
                Only AI players have finished this one so far.
              </p>
            )}
            {(highScores[activeVariant] ?? []).map((h, i) => (
              <div
                key={`${h.at}-${h.name}-${i}`}
                className="flex items-center gap-3 px-4 py-2.5"
              >
                <span className="w-6 font-mono text-sm text-muted">
                  {i + 1}
                </span>
                <span className="flex-1 truncate font-semibold">{h.name}</span>
                <span className="text-xs text-muted">
                  {dateFmt.format(h.at)}
                </span>
                <span
                  className={cn(
                    'w-16 text-right font-mono text-base tabular-nums',
                    i === 0 && 'text-accent'
                  )}
                >
                  {h.score}
                </span>
              </div>
            ))}
          </Card>
        </section>
      )}

      <section>
        <h2 className="mb-3 font-display text-lg tracking-wide">
          Recent games
        </h2>
        <ol className="space-y-2">
          {records.slice(0, 30).map(r => {
            const v = getVariant(r.variantId);
            return (
              <li key={r.id}>
                <Card className="p-3 sm:p-4">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-display text-sm tracking-wide">
                      {v.name}
                    </span>
                    <span className="text-xs text-muted">
                      {dateFmt.format(r.endedAt)}
                    </span>
                  </div>
                  <ul className="mt-2 space-y-1">
                    {r.players.map((p, i) => (
                      <li
                        key={`${p.name}-${i}`}
                        className="flex items-center gap-2 text-sm"
                      >
                        <span
                          className={cn(
                            'flex-1 truncate',
                            p.won ? 'font-semibold text-accent' : 'text-text'
                          )}
                        >
                          {p.name}
                          {p.kind === 'ai' && (
                            <span className="ml-1 text-xs text-muted">AI</span>
                          )}
                          {p.won && (
                            <span className="ml-2 text-xs uppercase tracking-wider text-accent">
                              Won
                            </span>
                          )}
                        </span>
                        <span className="font-mono tabular-nums">
                          {p.score}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </li>
            );
          })}
        </ol>
      </section>

      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent>
          <DialogTitle>Clear all stats?</DialogTitle>
          <DialogDescription>
            High scores and game history on this device will be removed.
          </DialogDescription>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setClearOpen(false)}>
              Keep stats
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                clear();
                setClearOpen(false);
              }}
            >
              Clear stats
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: number }) {
  return (
    <Card className="p-3 sm:p-4">
      <p className="truncate text-xs text-muted">{label}</p>
      <p className="mt-1 font-mono text-2xl tabular-nums text-accent sm:text-3xl">
        {value}
      </p>
    </Card>
  );
}
